import {
  containsLiquidVariable,
  isBareLiquidVariable,
  isValidUrlWithVariables,
} from "@circle-react-uikit/Form/LinkInput/liquidVariables";
import { prependProtocol } from "@circle-react-uikit/Form/LinkInput/prependProtocol";
import * as yup from "yup";

import { t } from "@/i18n-js/instance";

const localeNamespace = "settings.emails.builder";

// TipTap stores only #RRGGBB. Expand CSS shorthand before that check.
const transformColor = (value: unknown) => {
  if (typeof value !== "string" || value.length !== 4) return value;

  const [, r, g, b] = value;
  return `#${r}${r}${g}${g}${b}${b}`;
};

const colorSchema = yup
  .string()
  .transform(transformColor)
  .matches(
    /^#[0-9A-Fa-f]{6}$/,
    t([localeNamespace, "errors.color_is_required"]),
  )
  .required(t([localeNamespace, "errors.color_is_required"]));

export const getValidationSchema = (isWithinEmailEditor?: boolean) =>
  yup.object().shape({
    label: yup
      .string()
      .required(t([localeNamespace, "errors.label_is_required"]))
      // Variables only substitute in emails; reject them elsewhere so they don't render raw.
      .test(
        "no-off-surface-variable",
        t([localeNamespace, "errors.label_variable_not_supported"]),
        value =>
          Boolean(isWithinEmailEditor) || !containsLiquidVariable(value ?? ""),
      ),
    url: yup
      .string()
      .transform((_, value) =>
        typeof value === "string" && isBareLiquidVariable(value)
          ? value.trim()
          : prependProtocol(value, { allowMailTo: true }),
      )
      .test(
        "is-url-or-mailto",
        t([localeNamespace, "errors.url_is_invalid"]),
        function (value) {
          if (!value) return true;
          if (value.toLowerCase().startsWith("mailto:")) {
            // Validate only the address, ignoring any ?subject=/?body= query — matches
            // the backend MAILTO_FORMAT, which accepts mailto links with query params.
            const emailAddress = value.replace(/^mailto:/i, "").split("?")[0];
            const isValidEmail = yup.string().email().isValidSync(emailAddress);
            return (
              isValidEmail ||
              this.createError({
                message: t([localeNamespace, "errors.mailto_is_invalid"]),
              })
            );
          }
          // Variables only substitute in emails; reject a variable URL elsewhere.
          if (containsLiquidVariable(value)) {
            return (
              Boolean(isWithinEmailEditor) && isValidUrlWithVariables(value)
            );
          }
          return yup.string().url().isValidSync(value);
        },
      )
      .required(t([localeNamespace, "errors.url_is_required"])),
    color: colorSchema,
    may_lead_to_purchase: yup.boolean(),
    ...(isWithinEmailEditor && {
      text_color: colorSchema,
    }),
  });
